import React, { Component } from 'react';
import styled, { css } from 'styled-components';
import { PropTypes } from 'prop-types';
import ImageSliderContainer from 'containers/ImageSliderContainer';
import RoomDetailsButton from './RoomDetailsButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Container,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  Badge
} from 'reactstrap';
import RoomListItemActions from './RoomListItemActions';
import { ROOMS, NO_AVAILABILITY } from 'utils/constants';
import { ROOM_IMAGES, getRoomImages } from 'utils/images';
import { gaEvent } from 'utils/googleAnalytics';
import { ifProp, prop } from 'styled-tools';
import { pageShape } from 'utils/shapes';

const RoomDetailsModal = styled(Modal)`
  ${ifProp(
    'page',
    css`
      position: fixed;
      height: ${prop('page.height')}px;
      top: ${prop('page.scrollY')}px;
      min-height: unset;
      margin: 0;
      width: 100%;
      padding: 0.5em;
    `
  )}

  .modal-content {
    border-color: #aaa;
    box-shadow: 0px 0px 10px #888;

    ul {
      list-style-type: square;
      margin-top: 0;
      margin-bottom: 0;

      a {
        cursor: pointer;
        font-weight: bold;
      }
    }

    ul.with-icons {
      list-style-type: none;
      font-weight: bold;
      line-height: 2em;
      margin-left: 0;

      li {
        margin-left: 2.14285714em;
        margin-right: 0.5em;
        padding-left: 0.2em;
        display: inline-block;
      }

      .fa-li {
        font-size: 0.8em;
      }

      .fa-li.fa-layers {
        top: 0.6em;
      }
    }

    h3 {
      color: ${props => props.theme.h5.color};
      margin-top: 1em;
    }

    .modal-header {
      border-bottom: none;
      padding: 0;

      .close {
        margin: 0;
        padding: 0.5rem 0.75rem;
      }
    }

    .modal-body {
      padding-top: 0;
    }

    @media screen and (min-width: ${props => props.theme.breakpoints.small}) {
      ul {
        margin-top: 1em;
      }
    }

    @media screen and (min-width: ${props => props.theme.breakpoints.large}) {
      h3 {
        margin-top: 0;
      }
    }
  }
`;

const RoomADAccessibleModal = styled(Modal)`
  ${ifProp(
    'page',
    css`
      position: fixed;
      height: ${prop('page.height')}px;
      top: ${prop('page.scrollY')}px;
      min-height: unset;
      margin: 0;
      width: 100%;
      padding: 0.5em;
    `
  )}

  .modal-content {
    border-color: #aaa;

    h1 {
      color: ${props => props.theme.h5.color};
      font-size: 1.5rem;
      font-weight: 300;
      padding: 0.5rem 0 0.625rem 0;
    }

    .modal-body {
      padding-top: 0;
    }
  }
`;

const StyledModalHeading = styled(Row)`
  cursor: pointer;
  padding-top: 1rem;
  padding-bottom: 0.625rem;
`;

const ADAccessibleLink = styled.a`
  &&& {
    color: ${props => props.theme.buttons.primary.backgroundColor};
    font-size: 0.9rem;
    text-decoration: underline;
    font-weight: ${props => props.theme.fontBold};

    &:hover {
      cursor: pointer;
      color: ${props => props.theme.buttons.primary.backgroundColor};
      text-decoration: underline;
    }
  }
`;

class RoomListItem extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showRoomDetailsModal: false,
      showRoomADAccessibleModal: false,
      selectedRoom: null,
      previousPagePosition: null
    };
  }

  static getDerivedStateFromProps(props, state) {
    const { page } = props;
    const { showRoomDetailsModal, previousPagePosition } = state;

    return {
      previousPagePosition: showRoomDetailsModal ? previousPagePosition : page
    };
  }

  toggleRoomDetailsModal = room => {
    const { showRoomDetailsModal } = this.state;

    if (!showRoomDetailsModal) {
      // Record GA event.
      gaEvent({
        category: 'Hotel Booking',
        action: 'Viewed Room Details',
        label: room.roomType.roomTypeDescription
      });
    }

    this.setState({
      showRoomDetailsModal: !showRoomDetailsModal,
      selectedRoom: room
    });
  };

  toggleADAccessibleRoomModal = () => {
    const { showRoomADAccessibleModal } = this.state;

    this.setState({
      showRoomADAccessibleModal: !showRoomADAccessibleModal
    });
  };

  render() {
    const {
      room,
      rewards,
      subTotal,
      originalSubtotal,
      property,
      onSelect,
      index,
      page,
      isWaitListBooking,
      waitListBookingReason
    } = this.props;

    const {
      showRoomDetailsModal,
      showRoomADAccessibleModal,
      selectedRoom,
      previousPagePosition
    } = this.state;

    const nightlyAverage =
      subTotal / room.minRoomRate.expectedCharges.chargesForPostingDate.length;
    const originalNightlyAverage = originalSubtotal
      ? originalSubtotal /
        room.rackRoomRate.expectedCharges.chargesForPostingDate.length
      : null;

    const roomInfo = ROOMS[property][room.roomType.roomTypeCodes.roomTypeCode];

    if (!roomInfo) {
      return null;
    }

    const images = getRoomImages(
      property,
      roomInfo.roomType,
      room.roomType.roomTypeCodes.roomTypeCode
    );
    const waitlistComp = waitListBookingReason?.value === 'COMP';

    return (
      <React.Fragment>
        <Row>
          <Col
            xs={12}
            sm={3}
            className={`room-type-${
              room ? room.roomType.roomTypeCodes.roomTypeCode : ''
            }`}
          >
            <div
              className="room-image"
              onClick={this.toggleRoomDetailsModal.bind(this, room)}
            >
              {images && images[0] && <img src={images[0]} alt="room" />}
              <div className="image-info">
                <span>{images.length}</span>
                <span className="sr-only"> room images available</span>
                <FontAwesomeIcon icon="camera" role="presentation" />
              </div>
            </div>
          </Col>
          <Col xs={12} sm={6} className="room-details">
            <h4>{room.roomType.roomTypeDescription || roomInfo.name}</h4>
            <p className="room-description">{roomInfo.description || ''}</p>
            <RoomDetailsButton
              onClick={this.toggleRoomDetailsModal}
              room={room}
            />
          </Col>
          <Col xs={12} sm={3} className="actions subtotal">
            <RoomListItemActions
              room={room}
              subTotal={subTotal}
              originalSubtotal={originalSubtotal}
              nightlyAverage={nightlyAverage}
              originalNightlyAverage={originalNightlyAverage}
              waitlistComp={waitlistComp}
              rewards={rewards}
              onSelect={onSelect}
              isWaitListBooking={isWaitListBooking}
              index={index}
            />
          </Col>
        </Row>

        <RoomDetailsModal
          isOpen={showRoomDetailsModal}
          toggle={this.toggleRoomDetailsModal}
          backdrop={false}
          size="lg"
          page={previousPagePosition}
          centered={page === null}
          autoFocus
        >
          <ModalHeader toggle={this.toggleRoomDetailsModal} tag="span" />
          <ModalBody>
            <Container fluid>
              <Row>
                <Col xs={12} lg={6}>
                  <div style={{ height: '14em' }}>
                    <ImageSliderContainer
                      images={images}
                      overlayButtons
                      settings={{
                        slidesToShow: 1,
                        variableWidth: false
                      }}
                    />
                  </div>
                </Col>
                <Col xs={12} lg={6}>
                  <h3>
                    {selectedRoom &&
                      selectedRoom.roomType &&
                      selectedRoom.roomType.roomTypeDescription}
                  </h3>
                  <p className="room-description">
                    {roomInfo.description || ''}
                  </p>
                  {roomInfo.features &&
                    roomInfo.features.length > 0 &&
                    roomInfo.features.map(feature => {
                      const featureLowercase = feature
                        ? feature.toLowerCase()
                        : '';

                      return (
                        featureLowercase.indexOf(
                          'ada accessible rooms available'
                        ) > -1 && (
                          <ADAccessibleLink
                            onClick={this.toggleADAccessibleRoomModal}
                          >
                            {feature}
                          </ADAccessibleLink>
                        )
                      );
                    })}
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <ul className="fa-ul with-icons">
                    {(true || roomInfo.accessible) && (
                      <li>
                        <span className="fa-li">
                          <FontAwesomeIcon
                            icon="wheelchair"
                            mask="circle"
                            transform="shrink-7"
                            size="2x"
                            role="presentation"
                          />
                        </span>
                        Accessible
                      </li>
                    )}
                    {!roomInfo.smoking && (
                      <li>
                        <span
                          className="fa-li fa-layers fa-fw"
                          role="presentation"
                        >
                          <FontAwesomeIcon
                            icon="circle"
                            size="2x"
                            role="presentation"
                          />
                          <FontAwesomeIcon
                            icon="smoking"
                            transform="shrink-5"
                            inverse
                            role="presentation"
                          />
                          <FontAwesomeIcon
                            icon="ban"
                            transform="grow-8"
                            inverse
                            role="presentation"
                          />
                        </span>{' '}
                        Non-Smoking
                      </li>
                    )}
                  </ul>
                </Col>
              </Row>
              {roomInfo.features && roomInfo.features.length > 0 && (
                <Row>
                  <Col xs={12} sm={6}>
                    <ul>
                      {roomInfo.features
                        .slice(0, Math.floor(roomInfo.features.length / 2))
                        .map(feature => (
                          <li key={`feature-${feature}`}>{feature}</li>
                        ))}
                    </ul>
                  </Col>
                  <Col xs={12} sm={6}>
                    <ul>
                      {roomInfo.features
                        .slice(
                          Math.floor(roomInfo.features.length / 2),
                          roomInfo.features.length
                        )
                        .map(feature => {
                          const featureLowercase = feature
                            ? feature.toLowerCase()
                            : '';

                          const featureName =
                            featureLowercase.indexOf(
                              'ada accessible rooms available'
                            ) > -1 ? (
                              <a
                                onClick={() =>
                                  this.toggleADAccessibleRoomModal()
                                }
                              >
                                {feature}
                              </a>
                            ) : (
                              feature
                            );

                          return (
                            <li key={`feature-${feature}`}>{featureName}</li>
                          );
                        })}
                    </ul>
                  </Col>
                </Row>
              )}
            </Container>
          </ModalBody>
        </RoomDetailsModal>

        <RoomADAccessibleModal
          isOpen={showRoomADAccessibleModal}
          toggle={this.toggleADAccessibleRoomModal}
          backdrop={false}
          page={previousPagePosition}
          centered={page === null}
          size="lg"
          autoFocus
        >
          <ModalBody>
            <Container fluid>
              <StyledModalHeading>
                <Col sm={12}>
                  <FontAwesomeIcon
                    icon="arrow-left"
                    role="presentation"
                    onClick={this.toggleADAccessibleRoomModal}
                  />
                </Col>
              </StyledModalHeading>
              <Row>
                <Col sm={12}>
                  <h1>Accessible Room and the Housekeeping Features</h1>
                </Col>
              </Row>
              <Row>
                <Col sm={12}>
                  <p>
                    <b>Features of Accessible Rooms*:</b>
                  </p>
                  <ul>
                    <li>Located closer to the elevators</li>
                    <li>Doorbell</li>
                    <li>Light Flash Alarm</li>
                    <li>Lowered Sinks</li>
                    <li>
                      Lowered Beds
                      <ul>
                        <li>More space in between the beds</li>
                      </ul>
                    </li>
                    <li>Roll-in shower with shower curtain</li>
                    <li>Grab Bars in the shower and around toilet</li>
                    <li>Folding seat in shower</li>
                    <li>Hand-held shower handles</li>
                    <li>Towel holders are lower in the bathroom</li>
                    <li>No door to closet</li>
                    <li>
                      Two shelves (lowered)
                      <ul>
                        <li>
                          Hangers, Blanket, pillow, hairdryer is on lowest shelf
                        </li>
                      </ul>
                    </li>
                  </ul>
                  <p>
                    <b>Housekeeping Accessible Amenities*:</b>
                  </p>
                  <ul>
                    <li>
                      Shower Chair available upon request
                      <ul>
                        <li>Available for non-accessible shower</li>
                      </ul>
                    </li>
                  </ul>
                  <p>
                    <sub>* Based on availability</sub>
                  </p>
                </Col>
              </Row>
            </Container>
          </ModalBody>
        </RoomADAccessibleModal>
      </React.Fragment>
    );
  }
}

RoomListItem.propTypes = {
  room: PropTypes.object,
  subTotal: PropTypes.number,
  originalSubtotal: PropTypes.number,
  property: PropTypes.string,
  rewards: PropTypes.array,
  onSelect: PropTypes.func,
  index: PropTypes.number,
  page: pageShape,
  isWaitListBooking: PropTypes.bool,
  waitListBookingReason: PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  })
};

export default RoomListItem;
